import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TCareConfigurationNotification } from '../types/notification.type';

export type TGetCareConfigurationNotificationResponse = ApiResponse<TCareConfigurationNotification>;

export const getCareConfigurationNotification = async (): Promise<TGetCareConfigurationNotificationResponse> => {
  const url = `/notifications/care-configuration`;

  return axiosInstance.get(url);
};

export const GET_CARE_CONFIGURATION_NOTIFICATION_QUERY_KEY = 'get-care-configuration-notification';

export const getCareConfigurationNotificationQueryOptions = <TData = TGetCareConfigurationNotificationResponse>(
  select?: (data: TGetCareConfigurationNotificationResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_CARE_CONFIGURATION_NOTIFICATION_QUERY_KEY],
    queryFn: () => getCareConfigurationNotification(),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useCareConfigurationNotificationQuery<TData>(options: {
  queryConfig?: QueryConfig<typeof getCareConfigurationNotificationQueryOptions<TData>>;
  select: (data: TGetCareConfigurationNotificationResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useCareConfigurationNotificationQuery(options: {
  queryConfig?: QueryConfig<
    typeof getCareConfigurationNotificationQueryOptions<TGetCareConfigurationNotificationResponse>
  >;
}): ReturnType<typeof useQuery<TGetCareConfigurationNotificationResponse>>;

// Implementation
export function useCareConfigurationNotificationQuery<TData = TGetCareConfigurationNotificationResponse>({
  queryConfig,
  select,
}: {
  queryConfig?: QueryConfig<
    typeof getCareConfigurationNotificationQueryOptions<TData | TGetCareConfigurationNotificationResponse>
  >;
  select?: (data: TGetCareConfigurationNotificationResponse) => TData;
}) {
  return useQuery({
    ...getCareConfigurationNotificationQueryOptions(select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<
      TData extends TGetCareConfigurationNotificationResponse ? TGetCareConfigurationNotificationResponse : TData
    >
  >;
}
